import { getApiError } from '../lib/apiError';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { 
  Building2, Users, Package, ShoppingCart, Plus, Trash2, 
  Loader2, Eye, AlertTriangle, Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Empresas = () => {
  const { user } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    company_name: '',
    company_cnpj: '',
    company_phone: '',
    company_email: '',
    company_address: '',
    admin_name: '',
    admin_email: '',
    admin_password: ''
  });

  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetchEmpresas();
    }
  }, [user]);

  const fetchEmpresas = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/empresas`);
      setEmpresas(response.data);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.company_name || !formData.admin_name || !formData.admin_email || !formData.admin_password) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/empresas`, formData);
      toast.success('Empresa criada com sucesso!');
      setCreateDialogOpen(false);
      setFormData({
        company_name: '',
        company_cnpj: '',
        company_phone: '',
        company_email: '',
        company_address: '',
        admin_name: '',
        admin_email: '',
        admin_password: ''
      });
      fetchEmpresas();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao criar empresa'));
    } finally {
      setSaving(false);
    }
  };

  const handleViewDetails = async (empresa) => {
    try {
      const response = await axios.get(`${API_URL}/api/empresas/${empresa.id}`);
      setSelectedEmpresa(response.data);
      setDetailsDialogOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleDelete = async () => {
    if (!selectedEmpresa) return;

    setSaving(true);
    try {
      await axios.delete(`${API_URL}/api/empresas/${selectedEmpresa.id}`);
      toast.success('Empresa excluída com sucesso!');
      setDeleteDialogOpen(false);
      setSelectedEmpresa(null);
      fetchEmpresas();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao excluir empresa'));
    } finally {
      setSaving(false);
    }
  };

  const filteredEmpresas = empresas.filter(e => 
    e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.admin_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.cnpj?.includes(searchTerm)
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  // Verificação de acesso
  if (user?.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <Alert className="max-w-md">
          <AlertTriangle className="h-5 w-5" />
          <AlertDescription>
            Você não tem permissão para acessar esta página.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="empresas-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Gerenciar Empresas</h1>
          <p className="text-slate-500">Gerencie as empresas/clientes que utilizam o sistema</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="create-empresa-btn">
              <Plus size={18} />
              Nova Empresa
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="text-blue-600" size={20} />
                Criar Nova Empresa
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-4 border-b pb-4">
                <h3 className="font-semibold text-slate-700">Dados da Empresa</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Nome da Empresa *</Label>
                    <Input
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      placeholder="Nome da empresa"
                      data-testid="empresa-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>CNPJ</Label>
                    <Input
                      value={formData.company_cnpj}
                      onChange={(e) => setFormData({ ...formData, company_cnpj: e.target.value })}
                      placeholder="00.000.000/0000-00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefone</Label>
                    <Input
                      value={formData.company_phone}
                      onChange={(e) => setFormData({ ...formData, company_phone: e.target.value })}
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email da Empresa</Label>
                    <Input
                      type="email"
                      value={formData.company_email}
                      onChange={(e) => setFormData({ ...formData, company_email: e.target.value })}
                      placeholder="contato@empresa.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Endereço</Label>
                    <Input
                      value={formData.company_address}
                      onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
                      placeholder="Endereço completo"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700">Administrador da Empresa</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Nome do Administrador *</Label>
                    <Input
                      value={formData.admin_name}
                      onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                      placeholder="Nome completo"
                      data-testid="admin-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email de Acesso *</Label>
                    <Input
                      type="email"
                      value={formData.admin_email}
                      onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                      placeholder="admin@empresa.com"
                      data-testid="admin-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Senha *</Label>
                    <Input
                      type="password"
                      value={formData.admin_password}
                      onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                      placeholder="Senha de acesso"
                      data-testid="admin-password"
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreate} disabled={saving} className="gap-2">
                {saving && <Loader2 className="animate-spin" size={16} />}
                Criar Empresa
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <Input
          placeholder="Buscar empresa..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Building2 className="text-blue-600" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold">{empresas.length}</p>
                <p className="text-sm text-slate-500">Empresas Cadastradas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Users className="text-green-600" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {empresas.reduce((acc, e) => acc + (e.users_count || 0), 0)}
                </p>
                <p className="text-sm text-slate-500">Total de Usuários</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <ShoppingCart className="text-purple-600" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {empresas.filter(e => new Date(e.created_at) > new Date(Date.now() - 30*24*60*60*1000)).length}
                </p>
                <p className="text-sm text-slate-500">Novos (30 dias)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Empresas</CardTitle>
          <CardDescription>Empresas que utilizam o sistema RPB</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-blue-600" size={32} />
            </div>
          ) : filteredEmpresas.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Building2 size={48} className="mx-auto mb-4 text-slate-300" />
              <p>Nenhuma empresa cadastrada</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Administrador</TableHead>
                  <TableHead>Usuários</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmpresas.map((empresa) => (
                  <TableRow key={empresa.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{empresa.name}</p>
                        <p className="text-sm text-slate-500">{empresa.cnpj || '-'}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{empresa.admin_name || '-'}</p>
                        <p className="text-sm text-slate-500">{empresa.admin_email || '-'}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{empresa.users_count || 0} usuários</Badge>
                    </TableCell>
                    <TableCell>{formatDate(empresa.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetails(empresa)}
                          className="gap-1"
                        >
                          <Eye size={14} />
                          Detalhes
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedEmpresa(empresa);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="text-blue-600" size={20} />
              {selectedEmpresa?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedEmpresa && (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <Users className="mx-auto text-blue-600 mb-2" size={24} />
                  <p className="text-2xl font-bold">{selectedEmpresa.stats?.users_count || 0}</p>
                  <p className="text-xs text-slate-500">Usuários</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <Users className="mx-auto text-green-600 mb-2" size={24} />
                  <p className="text-2xl font-bold">{selectedEmpresa.stats?.clients_count || 0}</p>
                  <p className="text-xs text-slate-500">Clientes</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <Package className="mx-auto text-purple-600 mb-2" size={24} />
                  <p className="text-2xl font-bold">{selectedEmpresa.stats?.products_count || 0}</p>
                  <p className="text-xs text-slate-500">Produtos</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <ShoppingCart className="mx-auto text-orange-600 mb-2" size={24} />
                  <p className="text-2xl font-bold">{selectedEmpresa.stats?.sales_count || 0}</p>
                  <p className="text-xs text-slate-500">Vendas</p>
                </div>
              </div>

              {/* Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">CNPJ</Label>
                  <p className="font-medium">{selectedEmpresa.cnpj || '-'}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Telefone</Label>
                  <p className="font-medium">{selectedEmpresa.phone || '-'}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Email</Label>
                  <p className="font-medium">{selectedEmpresa.email || '-'}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Endereço</Label>
                  <p className="font-medium">{selectedEmpresa.address || '-'}</p>
                </div>
              </div>

              {/* Users */}
              <div>
                <h3 className="font-semibold mb-3">Usuários da Empresa</h3>
                <div className="space-y-2">
                  {selectedEmpresa.users?.map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">{u.name}</p>
                        <p className="text-sm text-slate-500">{u.email}</p>
                      </div>
                      <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>
                        {u.role}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={20} />
              Excluir Empresa
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Tem certeza que deseja excluir a empresa <strong>{selectedEmpresa?.name}</strong>?
            </p>
            <Alert className="mt-4 border-red-200 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-700">
                Esta ação é irreversível. Todos os dados da empresa serão excluídos permanentemente:
                usuários, clientes, produtos, vendas, notas fiscais, etc.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={saving}
              className="gap-2"
            >
              {saving && <Loader2 className="animate-spin" size={16} />}
              Excluir Empresa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Empresas;
